import { Gift } from 'lucide-react'
import React, { useState } from 'react'

import { useMutateReferralShareStatus } from 'src/features/business/referrals/api/referral-shares/use-mutate-referral-share-status'
import trashCan from 'src/shared/assets/icons/utils/trash-can.png'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { Button } from 'src/shared/components/ui/buttons/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/shared/components/ui/popups/dialog'
import { usePointsToast } from 'src/shared/components/ui/popups/toast/points-toast'
import { UserCard } from 'src/shared/features/users/components/cards/user-card'
import { useToast } from 'src/shared/hooks/use-toast'
import type { ReferralShare } from 'src/shared/schemas/referrals/referral-share'

interface ReferralShareActionModalProps {
  isOpen: boolean
  onClose: () => void
  referralShare: ReferralShare
}

type ReferralShareResponse = {
  senderTotalPoints: number | null
}

export const ReferralShareActionModal: React.FC<ReferralShareActionModalProps> = ({
  isOpen,
  onClose,
  referralShare,
}) => {
  const [step, setStep] = useState<'initial' | 'confirm' | 'success' | 'reject-confirm' | 'reject-success'>('initial')
  const updateStatus = useMutateReferralShareStatus()
  const isLoading = updateStatus.isPending
  const { showPointsToast } = usePointsToast()
  const { toast } = useToast()

  const handleApprove = async () => {
    if (step === 'initial') {
      setStep('confirm')
      return
    }

    if (isLoading) return

    try {
      const response = (await updateStatus.mutateAsync({
        id: referralShare.id,
        status: 'approved',
      })) as ReferralShareResponse
      if (response.senderTotalPoints) {
        showPointsToast({
          points: response.senderTotalPoints,
          message: (
            <div className="flex items-center gap-2">
              <span className="text-xl text-primary">
                You earned {response.senderTotalPoints}{' '}
                <PointIcon className="mx-1 inline-block size-5 align-text-bottom" /> for sending a referral!
              </span>
            </div>
          ),
        })
      }
      setStep('success')
    } catch (error) {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to send referral. Please try again later.',
      })
    }
  }

  const handleReject = () => {
    if (step === 'initial') {
      setStep('reject-confirm')
    } else if (step === 'reject-confirm') {
      if (isLoading) return
      updateStatus.mutateAsync({ id: referralShare.id, status: 'sender_denied' })
      setStep('reject-success')
    }
  }

  const renderContent = () => {
    switch (step) {
      case 'initial':
        return (
          <DialogContent className="mx-auto w-[90%] max-w-md rounded-xl p-5">
            <DialogHeader>
              <DialogTitle className="text-2xl">Send referral</DialogTitle>
              <DialogDescription>
                This will release all client info to {referralShare.receiverFirstName}.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <UserCard userId={referralShare.receiverId} buttonType="none" />
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="fadedDestructive" onClick={handleReject} className="flex-1">
                Reject
              </Button>
              <Button variant="fadedSecondary" onClick={handleApprove} className="flex-1">
                Refer
              </Button>
            </div>
          </DialogContent>
        )

      case 'confirm':
        return (
          <DialogContent className="mx-auto w-[90%] max-w-md rounded-xl p-5">
            <DialogHeader>
              <DialogTitle className="text-2xl">Send referral</DialogTitle>
              <DialogDescription>
                This will release all client info to {referralShare.receiverFirstName}.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <UserCard userId={referralShare.receiverId} buttonType="none" />
            </div>
            <div className="mt-4">
              <Button variant="secondary" onClick={handleApprove} className="w-full" isLoading={isLoading}>
                Confirm & send
              </Button>
            </div>
          </DialogContent>
        )

      case 'success':
        return (
          <DialogContent className="mx-auto w-[90%] max-w-md rounded-xl p-5 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
              <Gift className="size-8 text-primary" />
            </div>
            <DialogTitle className="text-2xl">Congratulations!</DialogTitle>
            <DialogDescription className="mt-2">
              You sent a referral to {referralShare.receiverFirstName} {referralShare.receiverLastName}
            </DialogDescription>
            <Button onClick={onClose} className="mt-4 w-full">
              Close
            </Button>
          </DialogContent>
        )

      case 'reject-confirm':
        return (
          <DialogContent className="mx-auto w-[90%] max-w-md rounded-xl p-5">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl">Are you sure?</DialogTitle>
              <DialogDescription className="pb-6 text-center">
                Do you really want to reject{' '}
                <span className="font-semibold">
                  {referralShare.receiverFirstName} {referralShare.receiverLastName}{' '}
                </span>
                ?
              </DialogDescription>
            </DialogHeader>
            <Button variant="default" onClick={handleReject} className="w-full" isLoading={isLoading}>
              Click to reject
            </Button>
          </DialogContent>
        )

      case 'reject-success':
        return (
          <DialogContent className="mx-auto w-[90%] max-w-md items-center rounded-xl p-5 text-center">
            <div className="mt-4 flex items-center justify-center">
              <img src={trashCan} alt="" className="h-24 self-center" />
            </div>
            <DialogTitle className="mb-4 text-2xl">You rejected {referralShare.receiverFirstName}</DialogTitle>
          </DialogContent>
        )
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {renderContent()}
    </Dialog>
  )
}
